// ============================================================================
// BOQ — standard clause library (admin editor for boq.clause_library)
// ============================================================================
// boq.clause_library is the firm-wide template set of inclusions, exclusions,
// assumptions and qualifications that seed each project's bill cover (copied,
// not linked, so editing here never changes already-issued bills). This hook
// is the global CRUD used by the Settings → Standard Clauses page. Writes are
// admin/supervisor-only, enforced by RLS; the UI surfaces friendly errors.
//
// Distinct from useBoqClauses, which manages the per-project project_clauses.
// ============================================================================

import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '../../supabaseClient';
import { formatSupabaseError } from '../../hooks/useSupabase';
import { CLAUSE_KINDS } from './useBoqClauses';

const SELECT = 'id, kind, text, sort_order, is_active';

const mapRow = (c) => ({
  id: c.id,
  kind: c.kind,
  text: c.text,
  sortOrder: c.sort_order,
  isActive: c.is_active,
});

const requireSession = async () => {
  const { data } = await supabase.auth.getSession();
  return !!data?.session;
};

export const useBoqClauseLibrary = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Editor shows inactive clauses too (with a toggle), so no is_active filter.
      const { data, error: fetchErr } = await supabase
        .schema('boq')
        .from('clause_library')
        .select(SELECT)
        .order('kind')
        .order('sort_order');
      if (fetchErr) throw fetchErr;
      setRows((data || []).map(mapRow));
    } catch (err) {
      console.error('[useBoqClauseLibrary] fetch failed', err);
      setError(formatSupabaseError(err, { action: 'fetch', entity: 'clause library' }));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // kind -> rows sorted by sort_order (stable for reorder maths)
  const byKind = useMemo(() => {
    const map = new Map();
    CLAUSE_KINDS.forEach((k) => map.set(k, []));
    rows.forEach((r) => {
      if (!map.has(r.kind)) map.set(r.kind, []);
      map.get(r.kind).push(r);
    });
    map.forEach((list) => list.sort((a, b) => a.sortOrder - b.sortOrder));
    return map;
  }, [rows]);

  const create = useCallback(async ({ kind, text }) => {
    if (!CLAUSE_KINDS.includes(kind)) return { success: false, error: 'Unknown clause type.' };
    const clean = (text || '').trim();
    if (!clean) return { success: false, error: 'Enter the clause text.' };
    try {
      if (!(await requireSession())) return { success: false, error: 'Please sign in.' };
      const peers = byKind.get(kind) || [];
      const nextOrder = peers.reduce((max, r) => Math.max(max, r.sortOrder), 0) + 1;
      const { data, error: insErr } = await supabase
        .schema('boq')
        .from('clause_library')
        .insert({ kind, text: clean, sort_order: nextOrder })
        .select(SELECT)
        .single();
      if (insErr) throw insErr;
      setRows((prev) => [...prev, mapRow(data)]);
      return { success: true };
    } catch (err) {
      console.error('[useBoqClauseLibrary] create failed', err);
      return { success: false, error: formatSupabaseError(err, { action: 'add', entity: 'clause' }) };
    }
  }, [byKind]);

  const update = useCallback(async (id, patch) => {
    const dbPatch = {};
    if (patch.text !== undefined) dbPatch.text = patch.text.trim();
    if (patch.isActive !== undefined) dbPatch.is_active = patch.isActive;
    if (patch.sortOrder !== undefined) dbPatch.sort_order = patch.sortOrder;
    if (Object.keys(dbPatch).length === 0) return { success: true };
    if (dbPatch.text === '') return { success: false, error: 'Clause text cannot be empty.' };
    try {
      if (!(await requireSession())) return { success: false, error: 'Please sign in.' };
      const { data, error: updErr } = await supabase
        .schema('boq')
        .from('clause_library')
        .update(dbPatch)
        .eq('id', id)
        .select(SELECT)
        .single();
      if (updErr) throw updErr;
      setRows((prev) => prev.map((r) => (r.id === id ? mapRow(data) : r)));
      return { success: true };
    } catch (err) {
      console.error('[useBoqClauseLibrary] update failed', err);
      return { success: false, error: formatSupabaseError(err, { action: 'update', entity: 'clause' }) };
    }
  }, []);

  const remove = useCallback(async (id) => {
    try {
      if (!(await requireSession())) return { success: false, error: 'Please sign in.' };
      const { error: delErr } = await supabase
        .schema('boq')
        .from('clause_library')
        .delete()
        .eq('id', id);
      if (delErr) throw delErr;
      setRows((prev) => prev.filter((r) => r.id !== id));
      return { success: true };
    } catch (err) {
      console.error('[useBoqClauseLibrary] delete failed', err);
      return { success: false, error: formatSupabaseError(err, { action: 'delete', entity: 'clause' }) };
    }
  }, []);

  // Swap a clause with its neighbour within the same kind (dir: -1 up, +1 down).
  // Persists both rows' sort_order so the order survives a refresh.
  const move = useCallback(async (id, dir) => {
    const row = rows.find((r) => r.id === id);
    if (!row) return { success: false, error: 'Clause not found.' };
    const peers = byKind.get(row.kind) || [];
    const idx = peers.findIndex((r) => r.id === id);
    const target = peers[idx + dir];
    if (!target) return { success: true }; // already at the edge
    try {
      if (!(await requireSession())) return { success: false, error: 'Please sign in.' };
      const { error: e1 } = await supabase.schema('boq').from('clause_library')
        .update({ sort_order: target.sortOrder }).eq('id', row.id);
      if (e1) throw e1;
      const { error: e2 } = await supabase.schema('boq').from('clause_library')
        .update({ sort_order: row.sortOrder }).eq('id', target.id);
      if (e2) throw e2;
      setRows((prev) => prev.map((r) => {
        if (r.id === row.id) return { ...r, sortOrder: target.sortOrder };
        if (r.id === target.id) return { ...r, sortOrder: row.sortOrder };
        return r;
      }));
      return { success: true };
    } catch (err) {
      console.error('[useBoqClauseLibrary] reorder failed', err);
      await fetchAll(); // resync if the two writes diverged
      return { success: false, error: formatSupabaseError(err, { action: 'reorder', entity: 'clause' }) };
    }
  }, [rows, byKind, fetchAll]);

  return { rows, byKind, loading, error, refresh: fetchAll, create, update, remove, move };
};

export default useBoqClauseLibrary;
